import { useEffect, useState } from "react";
import { toast } from "sonner";
import { FileDown, Sparkles, RefreshCw } from "lucide-react";
import api, { formatError } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";

export async function downloadQuoteExport(exp) {
  const res = await api.get(`/quote-exports/${exp.id}/pdf`, { responseType: "blob" });
  const url = URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${exp.code}_${exp.level}.pdf`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
}

const topicOf = (subject, clientName) => (subject || "").replace(/^\s*(subject\s*:)?\s*(quotation|quote|price quotation)\s*(for|of|-|–|:)?\s*/i, "").trim() || `your requirement${clientName ? ` at ${clientName}` : ""}`;

export function variantsFromL1(subject, clientName) {
  const t = topicOf(subject, clientName);
  return {
    L2: {
      subject: `Price Proposal – ${t}`,
      body: `Respected Sir/Madam,\nThank you for your enquiry regarding ${t}. We are glad to offer our most competitive rates for your kind consideration, as detailed below.`,
    },
    L3: {
      subject: `Commercial Offer for ${t}`,
      body: `Dear Sir/Madam,\nIn response to your requirement for ${t}, please find our commercial offer below. We assure you of quality products and timely delivery.`,
    },
  };
}

const sel = "h-9 w-full rounded-md border border-slate-300 bg-white px-2 text-[12.5px]";
const lbl = "text-[11px] uppercase tracking-wider font-semibold text-slate-600";

function CompanySelect({ value, onChange, companies, exclude, testid }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} className={sel} data-testid={testid}>
      <option value="">Select company…</option>
      {companies.filter((c) => !exclude.includes(c.id) || c.id === value).map((c) => (
        <option key={c.id} value={c.id}>{c.name}{c.has_letterhead ? "" : " (no letterhead)"}</option>
      ))}
    </select>
  );
}

export default function GenerateQuoteDialog({ open, onOpenChange, target }) {
  const [companies, setCompanies] = useState([]);
  const [l1, setL1] = useState("");
  const [companyGst, setCompanyGst] = useState("");
  const [showCompanyGst, setShowCompanyGst] = useState(true);
  const [needMore, setNeedMore] = useState(false);
  const [l2, setL2] = useState("");
  const [l3, setL3] = useState("");
  const [mode, setMode] = useState("fixed");
  const [pct, setPct] = useState("5");
  const [rMin, setRMin] = useState("3");
  const [rMax, setRMax] = useState("8");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [client, setClient] = useState({ client_name: "", client_address: "", client_pincode: "" });
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [vars, setVars] = useState({ L2: { subject: "", body: "" }, L3: { subject: "", body: "" } });
  const [varsEdited, setVarsEdited] = useState(false);
  useEffect(() => { if (!varsEdited) setVars(variantsFromL1(subject, client.client_name)); }, [subject, client.client_name, varsEdited]);
  const setVar = (lv, k, v) => { setVarsEdited(true); setVars((p) => ({ ...p, [lv]: { ...p[lv], [k]: v } })); };
  const fetchClient = async (silent) => {
    try {
      const { data } = await api.get(`/quote-requests/${target.id}/client-prefill`);
      setClient(data);
      if (!silent) toast.success("Fetched from CRM client");
    } catch (e) { if (!silent) toast.error(formatError(e.response?.data?.detail)); }
  };

  useEffect(() => {
    if (!open) return;
    setResult(null);
    fetchClient(true);
    setVarsEdited(false);
    setSubject(`Quotation for ${target?.client_name || ""}`.trim());
    setBody("Dear Sir/Madam,\nWith reference to your requirement, we are pleased to submit our best quotation as below.");
    api.get("/companies").then(({ data }) => setCompanies(data || [])).catch(() => {});
  }, [open]);

  const pickL1 = (id) => { setL1(id); setCompanyGst(companies.find((c) => c.id === id)?.gst_no || ""); };

  const generate = async () => {
    if (!client.client_name.trim() || !client.client_address.trim()) { toast.error("Enter client name and address"); return; }
    if (!/^\d{6}$/.test(client.client_pincode || "")) { toast.error("Enter a valid 6-digit pincode"); return; }
    if (!l1) { toast.error("Select the L1 company"); return; }
    if (needMore && (!l2 || !l3)) { toast.error("Select L2 and L3 companies"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/quote-requests/${target.id}/generate`, {
        ...client, subject, body,
        l2_subject: vars.L2.subject, l2_body: vars.L2.body, l3_subject: vars.L3.subject, l3_body: vars.L3.body,
        l1_company_id: l1, company_gst: companyGst, show_company_gst: showCompanyGst,
        need_l2_l3: needMore, l2_company_id: l2, l3_company_id: l3,
        increase_mode: mode, increase_pct: pct, random_min: rMin, random_max: rMax,
      });
      setResult(data);
      for (const e of data.exports) await downloadQuoteExport(e);
      toast.success(`Generated ${data.exports.length} quote${data.exports.length > 1 ? "s" : ""} · ${data.master_code}`);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg w-[calc(100vw-2rem)] [&>*]:min-w-0" data-testid="quote-generate-dialog">
        <DialogHeader><DialogTitle className="heading flex items-center gap-2 pr-6"><Sparkles size={16} className="text-rose-600" /> Generate Quote — {target?.client_name}</DialogTitle></DialogHeader>
        {result ? (
          <div className="space-y-2" data-testid="quote-generate-result">
            <div className="text-[12px]">Master Code: <span className="mono font-bold" data-testid="quote-master-code">{result.master_code}</span></div>
            {result.exports.map((e) => (
              <div key={e.id} className="flex items-center justify-between gap-2 rounded border border-slate-200 px-3 py-2 text-[12px]" data-testid={`quote-export-${e.level}`}>
                <span><span className={`mr-2 rounded px-1.5 py-0.5 text-[10px] font-bold ${e.level === "L1" ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-700"}`}>{e.level}</span><span className="font-semibold">{e.company_name}</span> · <span className="mono">{e.code}</span> · ₹ {Number(e.grand_total).toLocaleString("en-IN")}</span>
                <Button size="sm" variant="outline" className="h-7 px-2 text-[11px]" onClick={() => downloadQuoteExport(e).catch(() => toast.error("Download failed"))} data-testid={`quote-export-download-${e.level}`}><FileDown size={12} /></Button>
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-4 text-[12.5px] max-h-[62vh] overflow-y-auto pr-1">
            <div className="space-y-2 rounded-md border border-slate-200 p-3" data-testid="gen-client-block">
              <div className="flex items-center justify-between"><div className={lbl}>Client Details *</div>
                <button type="button" onClick={() => fetchClient(false)} className="inline-flex items-center gap-1 text-[11px] font-semibold text-indigo-700 hover:underline" data-testid="gen-fetch-crm"><RefreshCw size={11} /> Fetch from CRM</button>
              </div>
              <input value={client.client_name} onChange={(e) => setClient((c) => ({ ...c, client_name: e.target.value }))} placeholder="Client name" className={sel} data-testid="gen-client-name" />
              <textarea value={client.client_address} onChange={(e) => setClient((c) => ({ ...c, client_address: e.target.value }))} rows={2} placeholder="Address" className="w-full rounded-md border border-slate-300 px-2 py-1.5 text-[12.5px]" data-testid="gen-client-address" />
              <input value={client.client_pincode} onChange={(e) => setClient((c) => ({ ...c, client_pincode: e.target.value.replace(/\D/g, "").slice(0, 6) }))} placeholder="Pincode" inputMode="numeric" className={`${sel} w-32 mono`} data-testid="gen-client-pincode" />
            </div>
            <div className="space-y-2">
              <div><div className={lbl}>Subject</div><input value={subject} onChange={(e) => setSubject(e.target.value)} className={`${sel} mt-1`} data-testid="gen-subject" /></div>
              <div><div className={lbl}>Body</div><textarea value={body} onChange={(e) => setBody(e.target.value)} rows={3} className="mt-1 w-full rounded-md border border-slate-300 px-2 py-1.5 text-[12.5px]" data-testid="gen-body" /></div>
            </div>
            <div>
              <div className={lbl}>L1 Company (original quote) *</div>
              <div className="mt-1.5"><CompanySelect value={l1} onChange={pickL1} companies={companies} exclude={[l2, l3]} testid="gen-l1-company" /></div>
            </div>
            {l1 && (
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="font-semibold text-slate-700">Company GST No.</span>
                <span className="flex items-center gap-2">
                  <input value={companyGst} onChange={(e) => setCompanyGst(e.target.value.toUpperCase())} maxLength={15} className="h-8 w-44 rounded-md border border-slate-300 px-2 text-[12px] mono" data-testid="gen-company-gst" />
                  <label className="flex items-center gap-1 text-[11px] text-slate-600"><input type="checkbox" checked={showCompanyGst} onChange={(e) => setShowCompanyGst(e.target.checked)} data-testid="gen-company-gst-toggle" /> Show on PDF</label>
                </span>
              </div>
            )}
            <div className="flex items-center justify-between gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
              <span className="font-semibold text-slate-700">Do you need L2 &amp; L3 quotes?</span>
              <div className="inline-flex overflow-hidden rounded-md border border-slate-300 text-[11px] font-semibold" data-testid="gen-need-l2l3">
                {[["Yes", true], ["No", false]].map(([t, v]) => (
                  <button key={t} type="button" onClick={() => setNeedMore(v)} className={`px-3 py-1 ${needMore === v ? "bg-slate-900 text-white" : "bg-white text-slate-600"}`} data-testid={`gen-need-l2l3-${t.toLowerCase()}`}>{t}</button>
                ))}
              </div>
            </div>
            {needMore && (
              <div className="space-y-3 rounded-md border border-rose-100 bg-rose-50/30 p-3">
                <div className="grid gap-3 sm:grid-cols-2">
                  <div><div className={lbl}>L2 Company *</div><div className="mt-1.5"><CompanySelect value={l2} onChange={setL2} companies={companies} exclude={[l1, l3]} testid="gen-l2-company" /></div></div>
                  <div><div className={lbl}>L3 Company *</div><div className="mt-1.5"><CompanySelect value={l3} onChange={setL3} companies={companies} exclude={[l1, l2]} testid="gen-l3-company" /></div></div>
                </div>
                {["L2", "L3"].map((lv) => (
                  <div key={lv} className={`space-y-1.5 rounded-md border p-2 ${lv === "L2" ? "border-rose-200 bg-white" : "border-teal-200 bg-white"}`} data-testid={`gen-${lv.toLowerCase()}-letter`}>
                    <div className="flex items-center justify-between"><span className={lbl}>{lv} Subject &amp; Body <span className="normal-case font-normal text-slate-400">(auto from L1 · editable)</span></span>
                      {varsEdited && <button type="button" onClick={() => setVarsEdited(false)} className="text-[10.5px] font-semibold text-indigo-700 hover:underline" data-testid={`gen-${lv.toLowerCase()}-regen`}>Regenerate</button>}
                    </div>
                    <input value={vars[lv].subject} onChange={(e) => setVar(lv, "subject", e.target.value)} className={sel} data-testid={`gen-${lv.toLowerCase()}-subject`} />
                    <textarea value={vars[lv].body} onChange={(e) => setVar(lv, "body", e.target.value)} rows={2} className="w-full rounded-md border border-slate-300 px-2 py-1.5 text-[12px]" data-testid={`gen-${lv.toLowerCase()}-body`} />
                  </div>
                ))}
                <div>
                  <div className={lbl}>Rate Difference</div>
                  <div className="mt-1.5 flex gap-4 text-[12px]">
                    <label className="flex items-center gap-1.5"><input type="radio" checked={mode === "fixed"} onChange={() => setMode("fixed")} data-testid="gen-mode-fixed" /> Fixed increase</label>
                    <label className="flex items-center gap-1.5"><input type="radio" checked={mode === "random"} onChange={() => setMode("random")} data-testid="gen-mode-random" /> Random increase</label>
                  </div>
                  {mode === "fixed" ? (
                    <label className="mt-2 flex items-center gap-2 text-[12px]">Each level higher by
                      <input inputMode="decimal" value={pct} onChange={(e) => setPct(e.target.value.replace(/[^0-9.]/g, ""))} className="h-8 w-20 rounded-md border border-slate-300 px-2 text-right mono" data-testid="gen-fixed-pct" /> %
                    </label>
                  ) : (
                    <div className="mt-2 flex flex-wrap items-center gap-2 text-[12px]">Each product rises between
                      <input inputMode="decimal" value={rMin} onChange={(e) => setRMin(e.target.value.replace(/[^0-9.]/g, ""))} className="h-8 w-16 rounded-md border border-slate-300 px-2 text-right mono" data-testid="gen-random-min" /> % and
                      <input inputMode="decimal" value={rMax} onChange={(e) => setRMax(e.target.value.replace(/[^0-9.]/g, ""))} className="h-8 w-16 rounded-md border border-slate-300 px-2 text-right mono" data-testid="gen-random-max" /> %
                    </div>
                  )}
                  <div className="mt-1.5 text-[11px] text-slate-500">Every product rate in L2 will be higher than L1, and L3 higher than L2.</div>
                </div>
              </div>
            )}
          </div>
        )}
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="gen-close">{result ? "Done" : "Cancel"}</Button>
          {!result && <Button onClick={generate} disabled={busy} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="gen-submit"><FileDown size={14} className="mr-1" /> {busy ? "Generating…" : "Generate & Download"}</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
